'use client';

import { parseLampiranNumber } from '@justisio/shared/lib/roman-numeral';
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

export type LampiranClickHandler = (
  lampiranNumber: number,
  pageNumber?: number,
) => void;

interface LampiranReferenceTextProps {
  text: string;
  onLampiranClick?: LampiranClickHandler;
  className?: string;
}

const LAMPIRAN_PATTERN = /Lampiran\s+([IVXLCDM]+|\d+)(?:\s+(?:hal\.?|halaman)\s*(\d+))?/gi;

export function parseLampiranReferences(text: string): Array<{
  text: string;
  isReference: boolean;
  lampiranNumber?: number;
  pageNumber?: number;
  startIndex: number;
  endIndex: number;
}> {
  const segments: Array<{
    text: string;
    isReference: boolean;
    lampiranNumber?: number;
    pageNumber?: number;
    startIndex: number;
    endIndex: number;
  }> = [];

  LAMPIRAN_PATTERN.lastIndex = 0;

  const matches: Array<{
    start: number;
    end: number;
    lampiranNumber: number;
    pageNumber?: number;
  }> = [];

  let match;
  while ((match = LAMPIRAN_PATTERN.exec(text)) !== null) {
    const lampiranNum = parseLampiranNumber(match[1]);
    if (lampiranNum !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        lampiranNumber: lampiranNum,
        pageNumber: match[2] ? parseInt(match[2], 10) : undefined,
      });
    }
  }

  if (matches.length === 0) {
    return [{ text, isReference: false, startIndex: 0, endIndex: text.length }];
  }

  let lastIndex = 0;
  for (const refMatch of matches) {
    if (refMatch.start > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, refMatch.start),
        isReference: false,
        startIndex: lastIndex,
        endIndex: refMatch.start,
      });
    }

    segments.push({
      text: text.slice(refMatch.start, refMatch.end),
      isReference: true,
      lampiranNumber: refMatch.lampiranNumber,
      pageNumber: refMatch.pageNumber,
      startIndex: refMatch.start,
      endIndex: refMatch.end,
    });

    lastIndex = refMatch.end;
  }

  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      isReference: false,
      startIndex: lastIndex,
      endIndex: text.length,
    });
  }

  return segments;
}

export function LampiranReferenceText({
  text,
  onLampiranClick,
  className,
}: LampiranReferenceTextProps) {
  const segments = useMemo(() => parseLampiranReferences(text), [text]);

  if (!onLampiranClick) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((segment, index) => {
        if (!segment.isReference) {
          return <span key={index}>{segment.text}</span>;
        }

        return (
          <button
            key={index}
            onClick={() =>
              onLampiranClick(segment.lampiranNumber!, segment.pageNumber)
            }
            className={cn(
              'text-blue-600 hover:text-blue-800 hover:underline cursor-pointer',
              'font-medium',
            )}
            title={`Lampiran ${segment.lampiranNumber}${segment.pageNumber ? ` hal. ${segment.pageNumber}` : ''}`}
          >
            {segment.text}
          </button>
        );
      })}
    </span>
  );
}
